import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LibraryQueueItem, createLibraryIntake, fetchLibraryQueue, intakeGentleBatch3, intakeGrimmBatch2, intakeWizardOzPilot } from '../../api/libraryAdminApi';
import './LibraryAdmin.css';
import { getValidationPresentation } from './validationPresentation';

const STATES = ['', 'candidate', 'source_pinned', 'adapted', 'editorial_approved', 'art_approved', 'publish_ready', 'published', 'held'];
type LibrarySection = 'in_review' | 'published' | 'candidates' | 'cleanup' | 'held';
const SECTIONS: Record<LibrarySection, string> = { in_review: 'In Review', candidates: 'Candidates', cleanup: 'Catalog cleanup', published: 'Published', held: 'Held & tests' };

const humanize = (value: string) => value.replace(/_/g, ' ');

function librarySectionItems(items: LibraryQueueItem[], section: LibrarySection) {
  return items.filter((item) => (item.queue_section || (item.editorial_state === 'published' ? 'published' : 'in_review')) === section);
}

const LibraryAdminQueue: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<LibraryQueueItem[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [state, setState] = useState('');
  const [section, setSection] = useState<LibrarySection>('in_review');
  const [intakeType, setIntakeType] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [intakingBatch, setIntakingBatch] = useState(false);
  const [intakingGentleBatch, setIntakingGentleBatch] = useState(false);
  const [intakingWizardPilot, setIntakingWizardPilot] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const result = await fetchLibraryQueue({
          search,
          state,
          intake_type: intakeType,
        });
        setItems(result.items);
        setCounts(result.state_counts);
        setTotal(result.total);
        setError('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load the library queue');
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => window.clearTimeout(timer);
  }, [search, state, section, intakeType, refreshToken]);

  const addGrimmBatch2 = async () => {
    setIntakingBatch(true); setError('');
    try {
      await intakeGrimmBatch2();
      setRefreshToken((value) => value + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the approved Grimm batch');
    } finally {
      setIntakingBatch(false);
    }
  };

  const addGentleBatch3 = async () => {
    setIntakingGentleBatch(true); setError('');
    try {
      await intakeGentleBatch3();
      setRefreshToken((value) => value + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add Elves and Bremen for review');
    } finally {
      setIntakingGentleBatch(false);
    }
  };

  const addWizardPilot = async () => {
    setIntakingWizardPilot(true); setError('');
    try {
      await intakeWizardOzPilot();
      setRefreshToken((value) => value + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the Wizard of Oz pilot');
    } finally {
      setIntakingWizardPilot(false);
    }
  };

  const completed = useMemo(() => counts.published || 0, [counts]);
  const visibleItems = useMemo(() => librarySectionItems(items, section), [items, section]);


  return (
    <div className="classics-admin">
      <header className="classics-admin__hero">
        <div>
          <p className="classics-admin__eyebrow">Internal editorial workspace</p>
          <h1>Library Editorial</h1>
          <p>Review active drafts and see each title’s next step. Candidates, catalog cleanup, and published editions have their own queues.</p>
        </div>
        <div className="classics-admin__progress" aria-label={`${completed} of ${total} published`}>
          <strong>{completed}</strong><span>published</span><small>of {total} managed titles</small>
        </div>
      </header>

      <details className="library-intake">
        <summary>Add a library title</summary>
        <LibraryIntakeForm onCreated={(itemKey) => navigate(`/admin/library/${itemKey}`)} />
      </details>

      <button className="secondary" disabled={intakingBatch} onClick={() => void addGrimmBatch2()}>
        {intakingBatch ? 'Adding approved Grimm batch…' : 'Add approved Grimm batch to review'}
      </button>

      <button className="secondary" disabled={intakingGentleBatch} onClick={() => void addGentleBatch3()}>
        {intakingGentleBatch ? 'Adding Elves and Bremen…' : 'Add Elves and Bremen to review'}
      </button>

      <button className="secondary" disabled={intakingWizardPilot} onClick={() => void addWizardPilot()}>
        {intakingWizardPilot ? 'Adding Wizard of Oz pilot…' : 'Add Wizard of Oz pilot (3 bedtimes)'}
      </button>

      <nav className="library-sections" aria-label="Library publication status">
        {(Object.keys(SECTIONS) as LibrarySection[]).map((key) => <button key={key} type="button"
          className={section === key ? 'active' : ''} aria-current={section === key ? 'page' : undefined}
          onClick={() => { setSection(key); setState(''); }}>
          {SECTIONS[key]} <span>{librarySectionItems(items, key).length}</span>
        </button>)}
      </nav>

      <section className="classics-admin__filters" aria-label="Filter library queue">
        <label>
          <span>Find a title</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search all managed titles…" />
        </label>
        <label><span>Intake type</span><select value={intakeType} onChange={(e) => setIntakeType(e.target.value)}><option value="">All intake types</option><option value="public_domain_adaptation">Public-domain adaptation</option><option value="original">Original</option><option value="licensed">Licensed</option><option value="existing_story">Existing story</option></select></label>
        {section === 'in_review' && <label>
          <span>Editorial state</span>
          <select value={state} onChange={(e) => setState(e.target.value)}>
            {STATES.filter((value) => value !== 'published').map((value) => <option value={value} key={value || 'all'}>{value ? `${humanize(value)} (${counts[value] || 0})` : 'All review states'}</option>)}
          </select>
        </label>}
      </section>

      {error && <div className="classics-admin__notice classics-admin__notice--error">{error}</div>}
      {loading ? <p className="classics-admin__loading">Loading editorial queue…</p> : (
        <div className="classics-admin__table-wrap">
          <table className="classics-admin__table">
            <thead><tr><th>Title</th><th>Intake</th><th>Draft</th><th>Checks</th><th>Next action</th><th aria-label="Open" /></tr></thead>
            <tbody>
              {visibleItems.map((item) => {
                const validation = getValidationPresentation(item.validation_state, item.intake_type);
                return <tr key={item.catalog_id}>
                  <td><strong>{item.title}</strong><small>{humanize(item.category)}</small></td>
                  <td><span className="admin-pill">{humanize(item.intake_type)}</span></td>
                  <td><span className={`admin-status admin-status--${item.generation_state}`}>{humanize(item.generation_state)}</span></td>
                  <td><span className={`admin-status admin-status--${validation.statusClass}`}>{validation.label}</span></td>
                  <td><strong>{item.next_action || humanize(item.editorial_state)}</strong>
                    {item.text_approved && section === 'in_review' && <small>Story text approved</small>}
                    {item.missing_reviews?.length && section === 'in_review' ? <small>Remaining: {item.missing_reviews.join(', ')}</small> : null}
                    {item.preparation_note && <small>{item.preparation_note}</small>}
                  </td>
                  <td>{item.content_kind === 'serialized_book' || item.reviewable === false
                    ? <span className="admin-pill">Series</span>
                    : <Link className="classics-admin__open" to={`/admin/library/${item.item_key}`}>Review <span aria-hidden>→</span></Link>}</td>
                </tr>;
              })}
            </tbody>
          </table>
          {!visibleItems.length && <p className="classics-admin__empty">No titles in {SECTIONS[section].toLowerCase()} match these filters.</p>}
        </div>
      )}
    </div>
  );
};

const LibraryIntakeForm: React.FC<{ onCreated: (itemKey: string) => void }> = ({ onCreated }) => {
  const [title, setTitle] = useState('');
  const [itemKey, setItemKey] = useState('');
  const [intakeType, setIntakeType] = useState<'original' | 'licensed'>('original');
  const [brief, setBrief] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setSaving(true); setError('');
    try {
      const item = await createLibraryIntake({ item_key: itemKey, title, intake_type: intakeType, editorial_brief: brief });
      onCreated(item.item_key);
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not create title'); }
    finally { setSaving(false); }
  };
  return <form className="library-intake__form" onSubmit={(event) => void submit(event)}><label><span>Title</span><input required value={title} onChange={(e) => setTitle(e.target.value)} /></label><label><span>Stable key</span><input required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value={itemKey} onChange={(e) => setItemKey(e.target.value.toLowerCase())} placeholder="moonlit-kite" /></label><label><span>Intake type</span><select value={intakeType} onChange={(e) => setIntakeType(e.target.value as 'original' | 'licensed')}><option value="original">Original</option><option value="licensed">Licensed</option></select></label><label className="library-intake__brief"><span>Editorial brief</span><textarea required minLength={20} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Audience, recognizable premise, emotional arc, boundaries, and bedtime ending…" /></label>{error && <p className="classics-admin__notice classics-admin__notice--error">{error}</p>}<button className="primary" disabled={saving}>{saving ? 'Adding…' : 'Add to editorial queue'}</button></form>;
};

export { librarySectionItems };
export default LibraryAdminQueue;
